import { useTranslation } from "react-i18next";
import { Editor as EditorRef } from "@tinymce/tinymce-react";
import { Typography, useMediaQuery } from "@mui/material";
import { Dropdown, DropdownItem } from "react-nested-dropdown";
import "react-nested-dropdown/dist/styles.css";
import {
  SSMLMenuColors,
  SSMLTag,
  TTSCustomEditorColors,
} from "shared/constants";
import { sayAsMenuOptions } from "shared/models";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import theme from "styles/theme";
import {
  isHtmlViewAtom,
  selectedSayAsLangAtom,
  selectedValuesAtom,
  setNotificationAtom,
  shouldCloseMenuAtom,
} from "atoms";
import {
  handleCharacterUnitSelection,
  handleDateSelection,
  handleFractionsSelection,
  handleNumberSelection,
  handleOrdinalSelection,
  handleSpellOutSelection,
  handleTimeSelection,
} from "./handle-say-as-menu-selection";
import { useIsActiveSSMLTag, useSSMLTagRemover } from "shared/hooks/useSSMLTag";
import LanguageButtonMenu from "../../components/LanguageButtonMenu";
import { CustomTooltip } from "@web-applications/allinga-web-components";
import CustomInformationButton from "shared/components/CustomInformationButton";
import { useSSMLMenuDisabled } from "shared/utils/menu-disabling-rules";
import { useEffect, useState } from "react";
import {
  getChevronColor,
  getToolbarElementColor,
  isEditorEmpty,
  useEditorSelection,
} from "shared/utils/tts-editor-helper";
import { updateSayAsTag } from "shared/utils/tts-custom-editor";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { ReactComponent as ClearTagsIcon } from "assets/icons/clear-tags-icon.svg";
import { ReactComponent as SayAsIcon } from "assets/icons/say-as-icon.svg";

interface SayAsMenuProps {
  editorRef: React.RefObject<EditorRef | null>;
}

interface CustomDropdownButtonProps {
  isOpen: boolean;
  onClick: () => void;
}

const getCurrentSayAsAttributes = (editor: EditorRef["editor"]) => {
  if (!editor) return null;

  const node = editor.selection.getNode();
  const span = node.closest("span[data-ssml]");
  if (!span) return null;

  const dataSSML = span.getAttribute("data-ssml");
  if (!dataSSML) return null;

  const [, values] = dataSSML.split("=");
  if (!values) return null;

  const parts = values.split(", ").map((p) => p.trim());
  return {
    interpretAs: parts[0] || "",
    // If no language in the attribute, it means "automatic"
    lang: parts[1] || "automatic",
    format: parts[2] || "",
    detail: parts[3] || "",
    fullValue: values,
  };
};

export default function SayAsMenu({ editorRef }: SayAsMenuProps): JSX.Element {
  const { t } = useTranslation();
  // this is used to close the menu if tag cannot be inserted and an error message is shown but the menu is still open
  const [shouldCloseMenu, setShouldCloseMenu] = useAtom(shouldCloseMenuAtom);
  const setNotification = useSetAtom(setNotificationAtom);
  const isSayAsTagActive = useIsActiveSSMLTag(SSMLTag.SayAs);
  const selectedValues = useAtomValue(selectedValuesAtom);
  const [selectedSayAsLang, setSelectedSayAsLang] = useAtom(
    selectedSayAsLangAtom
  );
  const editorEmpty = isEditorEmpty(editorRef);
  const hasSelection = useEditorSelection(editorRef);
  const isHtmlView = useAtomValue(isHtmlViewAtom);
  const [isHovered, setIsHovered] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [currentAttributes, setCurrentAttributes] = useState<{
    interpretAs: string;
    lang: string;
    format: string;
    detail: string;
    fullValue: string;
  } | null>(null);
  const removeSSMLTag = useSSMLTagRemover(editorRef);
  const isVerySmallScreen = useMediaQuery("(max-width:800px)");
  const isSmallScreen = useMediaQuery("(max-width:1535px)");

  const isSelectionDisabled = useSSMLMenuDisabled(editorRef, SSMLTag.SayAs);

  const isDisabled =
    editorEmpty ||
    isHtmlView ||
    isSelectionDisabled ||
    (!hasSelection && !isSayAsTagActive);

  useEffect(() => {
    if (isSayAsTagActive && shouldCloseMenu) {
      setShouldCloseMenu(false);
    }
  }, [isSayAsTagActive, shouldCloseMenu, setShouldCloseMenu]);
  useEffect(() => {
    if (isSayAsTagActive && editorRef.current?.editor && currentAttributes) {
      if (currentAttributes.lang !== selectedSayAsLang) {
        updateSayAsTag(
          editorRef.current.editor,
          currentAttributes.interpretAs,
          selectedSayAsLang,
          currentAttributes.format,
          currentAttributes.detail
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSayAsLang, isSayAsTagActive, currentAttributes]);

  // Get the current tag's attributes whenever the active tag changes
  useEffect(() => {
    if (isSayAsTagActive && editorRef.current?.editor) {
      const attrs = getCurrentSayAsAttributes(editorRef.current.editor);
      setCurrentAttributes(attrs);
      if (attrs && attrs.lang) {
        setSelectedSayAsLang(attrs.lang);
      }
    } else {
      setCurrentAttributes(null);
      // If no tag is active, use "automatic" as default
      if (!isSayAsTagActive) {
        setSelectedSayAsLang("automatic");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSayAsTagActive]);

  useEffect(() => {
    if (!isSayAsTagActive) {
      setSelectedSayAsLang("automatic");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedValues.selectedLanguage]);

  const getToolTip = (tagName: string) => {
    const mainTooltip = t(
      `tts.toolbarMenu.sayAsMenu.${tagName.toLowerCase()}Tooltip`
    );
    // returns a list if exists, else a string of the key in translation
    const tooltipList = t(
      `tts.toolbarMenu.sayAsMenu.${tagName.toLowerCase()}TooltipList`,
      { returnObjects: true }
    ) as string[];

    // generate a list for tooltip else return the main tooltip string
    if (typeof tooltipList !== "string" && tooltipList.length > 0) {
      return (
        <div>
          {mainTooltip}
          <ul>
            {tooltipList.map((value) => (
              <li key={value}>{value}</li>
            ))}
          </ul>
        </div>
      );
    }

    return mainTooltip;
  };

  const setErrorMessage = (message: string) => {
    setNotification({
      message,
      severity: "error",
    });
  };

  const handleClearSayAsTag = () => {
    if (removeSSMLTag && isSayAsTagActive) {
      removeSSMLTag(SSMLTag.SayAs);
      setShouldCloseMenu(true);
    }
  };

  const validateSayAsContent = (
    caseOption: string,
    selectedContent: string
  ): { isValid: boolean; errorMessage?: string } => {
    switch (caseOption) {
      case "spell-out": {
        if (!selectedContent.trim()) {
          return { isValid: false, errorMessage: t("tts.generalErrorMessage") };
        }
        return { isValid: true };
      }

      case "fractions": {
        // Accept both simple fractions (1/2) and mixed fractions (1 1/2)
        const fractionRegex = /^\d+\/\d+$|^\d+\s+\d+\/\d+$/;
        if (!fractionRegex.test(selectedContent.trim())) {
          return {
            isValid: false,
            errorMessage: t("notifications.ttsSayAsInvalidNumberError"),
          };
        }
        return { isValid: true };
      }

      case "characters": {
        const charactersRegex = /^[a-zA-Z\s]+$/;
        if (!selectedContent.trim() || !charactersRegex.test(selectedContent)) {
          return {
            isValid: false,
            errorMessage: t("notifications.ttsSayAsInvalidCharactersError"),
          };
        }
        return { isValid: true };
      }

      case "date": {
        // Supports formats:
        // - Three-part with delimiters: dmy, mdy, ymd (e.g., 25.12.2024, 12/25/24, 2024-12-25)
        // - Two-part with delimiters: dm, md, ym, my (e.g., 25.12, 12/25, 2024-12, 12-2024)
        // - Compact (no delimiters): ddmmyyyy, yyyymmdd, ddmm, mmdd, yyyymm, mmyyyy
        // - Individual: d, m, y (e.g., 25, 12, 2024)
        const dateRegex =
          /^(?:\d{1,2}[./-]\d{1,2}[./-]\d{2,4}|\d{1,2}[./-]\d{1,2}|\d{1,2}[./-]\d{4}|\d{4}[./-]\d{1,2}|\d{1,2}|\d{4}|\d{6}|\d{8})$/;

        if (!dateRegex.test(selectedContent.trim())) {
          return {
            isValid: false,
            errorMessage: t("notifications.ttsSayAsInvalidDateError"),
          };
        }
        return { isValid: true };
      }

      case "time": {
        const timeRegex =
          /^\d{1,2}(?:[:. ]\d{1,2})?(?:[:. ]\d{1,2})?(?:\s?(?:AM|PM|A\.M\.|P\.M\.|A|P|am|pm|a\.m\.|p\.m\.))?$/i;
        if (!timeRegex.test(selectedContent.trim())) {
          return {
            isValid: false,
            errorMessage: t("notifications.ttsSayAsInvalidTimeError"),
          };
        }
        return { isValid: true };
      }

      case "ordinal": {
        // Allow plain numbers and ordinals, optionally with commas
        const numberRegex = /^\d{1,3}(,\d{3})*(st|nd|rd|th)?$/i;

        if (!numberRegex.test(selectedContent.trim())) {
          return {
            isValid: false,
            errorMessage: t("notifications.ttsSayAsInvalidNumberError"),
          };
        }
        return { isValid: true };
      }

      case "cardinal": {
        // allows:
        // - optional negative sign
        // - digits with optional comma/period grouping
        // - optional decimal
        // - optional % at end
        const numberRegex = /^-?\d{1,3}(?:[.,]\d{3})*(?:[.,]\d+)?%?$/;

        // also allow simple "123" without grouping
        const simpleNumberRegex = /^-?\d+(?:[.,]\d+)?%?$/;

        if (
          !numberRegex.test(selectedContent.trim()) &&
          !simpleNumberRegex.test(selectedContent.trim())
        ) {
          return {
            isValid: false,
            errorMessage: t("notifications.ttsSayAsInvalidNumberError"),
          };
        }

        return { isValid: true };
      }

      case "decimal": {
        // Accept numbers with optional thousands separators and decimals
        const numberRegex = /^(\d{1,3}(?:([.,])\d{3})*|\d+)([.,]\d+)?$/;

        if (!numberRegex.test(selectedContent.trim())) {
          return {
            isValid: false,
            errorMessage: t("notifications.ttsSayAsInvalidNumberError"),
          };
        }
        return { isValid: true };
      }

      case "digits": {
        const numberRegex = /^\d+$/;
        if (!numberRegex.test(selectedContent.trim())) {
          return {
            isValid: false,
            errorMessage: t("notifications.ttsSayAsInvalidNumberError"),
          };
        }
        return { isValid: true };
      }

      case "roman": {
        const numberRegex = /^[IVXLCDM]+$/i;
        if (!numberRegex.test(selectedContent.trim())) {
          return {
            isValid: false,
            errorMessage: t("notifications.ttsSayAsInvalidNumberError"),
          };
        }
        return { isValid: true };
      }

      case "currency": {
        // Matches: €44.02, USD 33.01, $100.50, 100 EUR, etc.
        const currencyRegex =
          /^(?:\d+(?:[.,]\d*)*\s*(?:€|¥|\$|£|Fr(?:andFr)?|CAD|SGD|USD|NZD|AUD|HKD|JPY|CHF|EUR|GBP)|(?:€|¥|\$|£|Fr(?:andFr)?|CAD|SGD|USD|NZD|AUD|HKD|JPY|CHF|EUR|GBP)\s*\d+(?:[.,]\d*)*)$/;
        if (!currencyRegex.test(selectedContent.trim())) {
          return {
            isValid: false,
            errorMessage: t("notifications.ttsSayAsInvalidCurrencyError"),
          };
        }
        return { isValid: true };
      }

      case "unit":
        return { isValid: true };

      default:
        return { isValid: true };
    }
  };

  const handleSayAsMenuClick = (selectedSayAsOption: string) => {
    const editor = editorRef.current?.editor;
    if (!editor) return;

    const selectedOption = selectedSayAsOption.toLowerCase();
    let caseOption = selectedOption;

    const dateIndex = sayAsMenuOptions.findIndex(
      (menu) => menu.menuName === "Date"
    );
    const timeIndex = sayAsMenuOptions.findIndex(
      (menu) => menu.menuName === "Time"
    );

    if (
      sayAsMenuOptions[dateIndex].menuOptions?.includes(selectedSayAsOption)
    ) {
      caseOption = "date";
    }
    if (
      sayAsMenuOptions[timeIndex].menuOptions?.includes(selectedSayAsOption)
    ) {
      caseOption = "time";
    }

    const selectedContent =
      editor.selection.getContent({ format: "text" }) || "";

    const needsValidation = [
      "fractions",
      "date",
      "time",
      "ordinal",
      "cardinal",
      "decimal",
      "digits",
      "roman",
      "currency",
      "characters",
    ];

    if (needsValidation.includes(caseOption)) {
      const validation = validateSayAsContent(caseOption, selectedContent);

      if (!validation.isValid) {
        setErrorMessage(
          validation.errorMessage || t("tts.generalErrorMessage")
        );
        setShouldCloseMenu(true);
        return;
      }
    }

    if (isSayAsTagActive) {
      try {
        const success = updateSayAsTag(
          editor,
          selectedOption,
          selectedSayAsLang
        );
        if (!success) {
          setErrorMessage(t("tts.generalErrorMessage"));
        }
      } catch (error) {
        setErrorMessage(t("tts.generalErrorMessage"));
      }

      setShouldCloseMenu(true);
      return;
    }

    let isTagAdded = false;

    switch (caseOption) {
      case "characters":
      case "unit":
        handleCharacterUnitSelection(
          selectedOption,
          selectedSayAsLang,
          editorRef
        );
        isTagAdded = true;
        break;

      case "spell-out":
        isTagAdded = handleSpellOutSelection(
          selectedOption,
          selectedContent,
          selectedSayAsLang,
          editorRef
        );
        break;

      case "fractions":
        isTagAdded = handleFractionsSelection(
          selectedContent,
          selectedSayAsLang,
          editorRef
        );
        break;

      case "date": {
        const index = selectedOption.indexOf("(");
        let outputFormat = "";
        if (index !== -1) {
          outputFormat = selectedSayAsOption
            .toLowerCase()
            .substring(0, index)
            .trim();
        }
        isTagAdded = handleDateSelection(
          selectedContent,
          outputFormat,
          selectedSayAsLang,
          editorRef
        );
        break;
      }
      case "time":
        isTagAdded = handleTimeSelection(
          selectedOption,
          selectedContent,
          selectedSayAsLang,
          selectedSayAsLang,
          editorRef
        );
        break;

      case "ordinal":
        isTagAdded = handleOrdinalSelection(
          selectedOption,
          selectedContent,
          editorRef,
          selectedSayAsLang
        );
        break;

      case "cardinal":
      case "decimal":
      case "digits":
      case "roman":
      case "currency":
        isTagAdded = handleNumberSelection(
          selectedOption,
          selectedContent,
          selectedSayAsLang,
          editorRef
        );
        break;

      default:
        setErrorMessage(t("tts.generalErrorMessage"));
        setShouldCloseMenu(true);
        return;
    }

    if (!isTagAdded && caseOption !== "characters" && caseOption !== "unit") {
      setErrorMessage(t("tts.generalErrorMessage"));
    }

    setShouldCloseMenu(true);
  };

  const handleDropdownButtonClick = () => {
    if (shouldCloseMenu) {
      setShouldCloseMenu(false);
    }
  };

  const createDropdownItems = () => {
    const menuItems = sayAsMenuOptions.map((menu) => {
      const isSelected =
        isSayAsTagActive &&
        currentAttributes?.interpretAs?.toLowerCase() ===
          menu.menuName.toLowerCase();

      if (menu.menuOptions && menu.menuOptions.length > 0) {
        let valueToCheck = currentAttributes?.interpretAs?.toLowerCase();

        if (menu.menuName === "Time" || menu.menuName === "Date") {
          const fullValue = currentAttributes?.fullValue;
          if (fullValue && typeof fullValue === "string") {
            const parts = fullValue.split(",").map((p) => p.trim());
            valueToCheck = parts[parts.length - 1]?.toLowerCase();
          }
        }

        const isParentMenuSelected =
          isSayAsTagActive &&
          menu.menuOptions.some((option) => {
            const optionKey = option.split(" ")[0].toLowerCase();
            return valueToCheck === optionKey;
          });

        return {
          className: isParentMenuSelected
            ? "selected-item"
            : `menu-item-${menu.menuName.toLowerCase()}`,
          label: (
            <span
              style={{
                display: "flex",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              {menu.menuName}
            </span>
          ),
          items: menu.menuOptions.map((option, optionIndex) => {
            const optionKey = option.split(" ")[0].toLowerCase();
            const isSubOptionSelected =
              isSayAsTagActive && valueToCheck === optionKey;

            return {
              label: (
                <span
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  {option}
                </span>
              ),
              className: isSubOptionSelected
                ? "selected-item"
                : `submenu-item-${optionIndex}`,
              onSelect: () => handleSayAsMenuClick(option),
            };
          }),
        };
      } else {
        return {
          label: (
            <CustomTooltip
              title={getToolTip(menu.menuName)}
              placement="right"
              component="span"
              forceShow
            >
              <span
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  width: "100%",
                  fontWeight: isSelected ? "bold" : "normal",
                }}
              >
                <Typography variant="body1" component="span">
                  {menu.menuName}
                </Typography>
                <span style={{ display: "inline-flex", marginLeft: "auto" }}>
                  <CustomInformationButton disableHover={true} />
                </span>
              </span>
            </CustomTooltip>
          ),
          className: isSelected
            ? "selected-item"
            : `menu-item-${menu.menuName.toLowerCase()}`,
          onSelect: () => handleSayAsMenuClick(menu.menuName),
        };
      }
    });

    // Add clear tag button if a say-as tag is active
    const clearButtonItem = {
      label: (
        <span className="clear-button-item">
          <Typography
            sx={{ marginRight: "8px" }}
            variant="body1"
            component="span"
          >
            {t("buttons.clearTags")}
          </Typography>
          <ClearTagsIcon />
        </span>
      ),
      onSelect: handleClearSayAsTag,
      className: "no-hover-clear-button",
    };

    const languageHeaderItem = {
      label: (
        <span
          style={{
            display: "flex",
            flexDirection: "row",
            gap: 8,
            justifyContent: "space-between",
          }}
        >
          <Typography
            variant="subtitle2"
            color="text.secondary"
            component="span"
          >
            {t("tts.toolbarMenu.sayAsMenu.menuHeader")}
          </Typography>
          <LanguageButtonMenu
            onLanguageClick={(selectedLanguage) =>
              setSelectedSayAsLang(selectedLanguage)
            }
            buttonText={t(
              `tts.toolbarMenu.languageMenu.${selectedSayAsLang.toLowerCase()}`
            )}
            currentTagLanguage={currentAttributes?.lang}
            menuZIndex={10003}
          />
        </span>
      ),
      disabled: true,
      className: "language-selector-header",
    };

    return isSayAsTagActive
      ? [languageHeaderItem, ...menuItems, clearButtonItem]
      : [languageHeaderItem, ...menuItems];
  };

  const getTooltipContent = () => {
    if (isSelectionDisabled) {
      return t("ssml.nesting.tooltip");
    }
    if (isDisabled && !isHtmlView) {
      return t("tts.toolbarMenu.disabledMenuTooltip");
    }
    return "";
  };

  const getTooltipPlacement = () => {
    if (isSelectionDisabled) {
      return "bottom";
    }
    return isVerySmallScreen ? "bottom" : "top";
  };

  const shouldShowTooltip =
    isSelectionDisabled ||
    (isDisabled && !editorEmpty && !isHtmlView) ||
    isSayAsTagActive;

  const tooltipContent = getTooltipContent();

  const CustomDropdownButton = ({
    isOpen,
    onClick,
  }: CustomDropdownButtonProps) => {
    // Track when dropdown opens/closes
    useEffect(() => {
      setIsDropdownOpen(isOpen);
    }, [isOpen]);

    return (
      <button
        type="button"
        onClick={() => {
          handleDropdownButtonClick();
          onClick();
        }}
        disabled={isDisabled}
        className="say-as-button"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        id="say-as-button"
        style={{
          margin: "0 4px",
          display: "flex",
          alignItems: "center",
          position: "relative",
          zIndex: isOpen ? 10000 : 1,
          isolation: "isolate",
          backgroundColor: isDisabled
            ? "transparent"
            : isSayAsTagActive
              ? SSMLMenuColors.ActiveBackground
              : "transparent",
          color: getToolbarElementColor(
            isHtmlView,
            editorEmpty,
            isHovered,
            isSayAsTagActive,
            isDisabled
          ),
          border: isSayAsTagActive
            ? `1px solid ${TTSCustomEditorColors.ToolbarActiveBorder}`
            : "transparent",
          cursor: isDisabled ? "default" : "pointer",
          padding: "8px",
          ...(isHovered && !editorEmpty && !isHtmlView && !isSayAsTagActive
            ? {
                backgroundColor: `${theme.palette.action.hover}`,
              }
            : {}),
        }}
      >
        {isSmallScreen ? (
          <SayAsIcon
            style={{
              display: "flex",
              color: getToolbarElementColor(
                isHtmlView,
                editorEmpty,
                isHovered,
                isSayAsTagActive,
                isDisabled
              ),
            }}
          />
        ) : (
          <>
            <SayAsIcon
              style={{
                marginRight: "4px",
                display: "flex",
                color: getToolbarElementColor(
                  isHtmlView,
                  editorEmpty,
                  isHovered,
                  isSayAsTagActive,
                  isDisabled
                ),
              }}
            />
            <Typography
              variant="subtitle2"
              color="text.secondary"
              component="span"
              sx={{
                color: getToolbarElementColor(
                  isHtmlView,
                  editorEmpty,
                  isHovered,
                  isSayAsTagActive,
                  isDisabled
                ),
              }}
            >
              {t("tts.toolbarMenu.sayAsMenu.title")}
            </Typography>
          </>
        )}
        <span
          style={{
            marginLeft: "8px",
            display: "flex",
          }}
        >
          {!isSmallScreen ? (
            isOpen ? (
              <KeyboardArrowUpIcon
                sx={{
                  width: 20,
                  height: 20,
                  color: getChevronColor(isHtmlView, editorEmpty, isDisabled),
                }}
              />
            ) : (
              <KeyboardArrowDownIcon
                sx={{
                  width: 20,
                  height: 20,
                  color: getChevronColor(isHtmlView, editorEmpty, isDisabled),
                }}
              />
            )
          ) : null}
        </span>
      </button>
    );
  };

  const dropdownComponent = (
    <div
      style={{
        position: "relative",
        zIndex: isDropdownOpen ? 10000 : 1,
        isolation: "isolate",
      }}
    >
      <Dropdown
        items={createDropdownItems() as unknown as DropdownItem<unknown>[]}
        containerWidth="345px"
        className="dropdown-item"
        key={`dropdown-${isSayAsTagActive}-${currentAttributes?.interpretAs}`}
      >
        {(props) => <CustomDropdownButton {...props} />}
      </Dropdown>
    </div>
  );

  return (
    <>
      {shouldShowTooltip && tooltipContent ? (
        <CustomTooltip
          placement={getTooltipPlacement()}
          title={tooltipContent}
          disableHoverListener={false}
          forceShow
        >
          <span style={{ display: "inline-block" }}>{dropdownComponent}</span>
        </CustomTooltip>
      ) : (
        dropdownComponent
      )}
    </>
  );
}
